import { useState } from 'react'
import { motion } from 'framer-motion'
import { Check } from 'lucide-react'
import { SimpleIcon } from '@adthlete/common'
import { PublicProfile, ProfileLink, Athlete, SocialChannel } from '@/types/public-profile'
import { sports } from '@/config'
import WorkWithMeModal from './WorkWithMeModal'

interface ProfilePreviewProps {
  profile: PublicProfile
  athlete: Athlete | null
  isFullPage?: boolean
  /** The athlete's Content Shop, for brands (only when it's open) */
  shopUrl?: string | null
}

const ProfilePreview = ({
  profile,
  athlete,
  isFullPage = false,
  shopUrl = null,
}: ProfilePreviewProps) => {
  const [showWorkWithMe, setShowWorkWithMe] = useState(false)

  // Get details
  const getSportName = () => {
    if (!athlete?.sports || athlete.sports.length === 0) return null
    const sportValue = athlete.sports[0]

    if (typeof sportValue === 'string') {
      return sportValue
    }

    // It's an ID (number), look it up
    if (typeof sportValue === 'number') {
      const sport = sports.find((s) => s.id === sportValue)
      return sport?.name || 'Athlete'
    }

    return null
  }

  const sportName = getSportName()

  const schoolName = athlete?.school?.name || null
  const classYear = profile.classYear || null

  const campaigns = profile.links?.campaigns || []
  const links = profile.links?.links || []
  const partners = profile.links?.partners || []
  const socialChannels =
    (profile as PublicProfile & { socialChannels?: SocialChannel[] }).socialChannels || []

  const hasContent =
    campaigns.length > 0 || links.length > 0 || partners.length > 0 || socialChannels.length > 0

  return (
    <div className={`relative ${isFullPage ? 'min-h-screen' : 'min-h-[600px] overflow-hidden'}`}>
      {/* Background - Fixed for full page, absolute for preview */}
      <div
        className={isFullPage ? 'fixed inset-0 -z-10' : 'absolute inset-0'}
        style={{
          background:
            'linear-gradient(135deg, #060B0F 0%, #0B2A32 35%, #007C8A 70%, #00A7B5 100%)',
        }}
      />

      {/* Background glow */}
      <div
        className={`${
          isFullPage ? 'fixed' : 'absolute'
        } inset-0 pointer-events-none overflow-hidden`}
      >
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[hsl(180_40%_25%)] rounded-full blur-[120px] opacity-40" />
      </div>

      <div className={`relative z-10 ${isFullPage ? 'max-w-[430px] mx-auto' : ''}`}>
        {/* Hero Image */}
        <motion.div
          className="w-full overflow-hidden"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-full overflow-hidden rounded-b-4xl bg-gray-200 flex items-center justify-center aspect-square">
            {profile.heroImageUrl ? (
              <img
                src={profile.heroImageUrl}
                alt="Hero"
                className="w-full h-full object-cover object-center"
              />
            ) : athlete?.avatar ? (
              <img
                src={athlete.avatar}
                alt={athlete.name}
                className="w-full h-full object-cover object-center"
              />
            ) : (
              <span className="text-[#FFFFFF]/40 text-sm">Hero Image</span>
            )}
          </div>
        </motion.div>

        {/* Profile Info */}
        <motion.div
          className={`flex flex-col items-center text-center px-4 ${
            isFullPage ? 'pt-6 pb-4' : 'pt-4 pb-3'
          }`}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
        >
          <h1
            className={`text-[#FFFFFF] mb-1 ${isFullPage ? 'text-3xl' : 'text-2xl font-bold'}`}
            style={isFullPage ? { fontWeight: 900 } : { fontWeight: 700 }}
          >
            {athlete?.name || 'Your Name'}
          </h1>

          {/* School & Sport */}
          {(schoolName || sportName) && (
            <p
              className="text-sm text-[#FFFFFF]/90 mb-0.5"
              style={{
                fontWeight: 500,
              }}
            >
              {[schoolName, sportName, isFullPage && classYear].filter(Boolean).join(' • ')}
            </p>
          )}

          {/* Class Year - Only show separately if not full page (full page merges it above) */}
          {classYear && !isFullPage && (
            <p className="text-xs text-[#FFFFFF]/60 uppercase tracking-widest mt-1">{classYear}</p>
          )}
        </motion.div>

        {/* Link Sections */}
        <div className="flex flex-col gap-4 px-4 pb-6 mt-2">
          {/* Campaigns */}
          {campaigns.length > 0 && <LinkSection title="Current Campaigns" items={campaigns} />}

          {/* Social Channels */}
          {socialChannels.length > 0 && <SocialChannelsSection channels={socialChannels} />}

          {/* Links */}
          {links.length > 0 && <LinkSection title="My Links" items={links} />}

          {/* Partners */}
          {partners.length > 0 && <LinkSection title="Official Brand Partners" items={partners} />}

          {/* Empty state */}
          {!hasContent && !isFullPage && (
            <motion.div
              className="py-8 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p className="text-[#FFFFFF]/50 text-sm">
                Add links to see them appear in your profile
              </p>
            </motion.div>
          )}
        </div>

        {/* Brands: the shop first, where they can see the content and book it */}
        <div className="space-y-2 px-4 pb-6">
          {shopUrl ? (
            <a
              href={shopUrl}
              className="block w-full rounded-xl bg-[#FFFFFF] py-3 text-center text-sm font-semibold text-[#0A1A1F] shadow-lg"
            >
              Brands: shop my content
            </a>
          ) : null}
          <motion.button
            className="w-full py-3 rounded-xl font-semibold text-sm transition-all shadow-lg shadow-[#C0FD72]/20"
            style={{
              background: 'linear-gradient(135deg, #C0FD72 0%, #9FE85A 100%)',
              color: '#0A1A1F',
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowWorkWithMe(true)}
          >
            Work With Me
          </motion.button>
        </div>

        {/* Footer */}
        <div className="px-4 pb-8 text-center">
          <p className="text-[#FFFFFF]/30 text-xs font-medium">Powered by Athios</p>
        </div>
      </div>

      <WorkWithMeModal
        isOpen={showWorkWithMe}
        onClose={() => setShowWorkWithMe(false)}
        username={profile.username}
        athleteName={athlete?.name || 'the athlete'}
      />
    </div>
  )
}

// Link Section Component
const LinkSection = ({ title, items }: { title: string; items: ProfileLink[] }) => {
  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Section Divider */}
      <div className="h-px bg-linear-to-r from-transparent via-foreground/20 to-transparent mb-3" />

      {/* Section Title */}
      <h2 className="text-xs font-semibold text-[#FFFFFF]/60 uppercase tracking-wider mb-2">
        {title}
      </h2>

      {/* Links */}
      <div className="flex flex-col gap-2">
        {items.map((item, index) => (
          <motion.a
            key={item.id}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#FFFFFF]/10 backdrop-blur-xs border border-[#FFFFFF]/10 hover:bg-[#FFFFFF]/15 transition-all"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            {item.brandLogo && (
              <div className="w-8 h-8 rounded-full bg-[#FFFFFF]/10 overflow-hidden shrink-0">
                <img
                  src={item.brandLogo}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    ;(e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              </div>
            )}
            <span className="text-[#FFFFFF] text-sm font-medium flex-1">{item.label}</span>
          </motion.a>
        ))}
      </div>
    </motion.section>
  )
}

const SocialChannelsSection = ({ channels }: { channels: SocialChannel[] }) => {
  const resolveSocialUrl = (channel: SocialChannel) => {
    const explicitUrl = String(channel.url || '').trim()
    if (/^https?:\/\//i.test(explicitUrl)) {
      return explicitUrl
    }

    const platformKey = String(channel.platformKey || channel.platform || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '')

    const rawHandle = String(channel.handle || '').trim()
    if (!rawHandle) return null

    if (/^https?:\/\//i.test(rawHandle)) {
      return rawHandle
    }

    const handle = rawHandle.replace(/^@/, '')

    switch (platformKey) {
      case 'instagram':
        return `https://www.instagram.com/${handle}`
      case 'tiktok':
        return `https://www.tiktok.com/@${handle}`
      case 'youtube':
        return `https://www.youtube.com/@${handle}`
      case 'facebook':
        return `https://www.facebook.com/${handle}`
      case 'snapchat':
        return `https://www.snapchat.com/add/${handle}`
      case 'linkedin':
        return `https://www.linkedin.com/in/${handle}`
      case 'twitter':
      case 'x':
      case 'xtwitter':
        return `https://x.com/${handle}`
      case 'twitch':
        return `https://www.twitch.tv/${handle}`
      case 'pinterest':
        return `https://www.pinterest.com/${handle}`
      case 'threads':
        return `https://www.threads.net/@${handle}`
      default:
        return null
    }
  }

  const toSimpleIconSlug = (platform: string) => {
    const key = String(platform || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '')

    switch (key) {
      case 'twitter':
      case 'x':
      case 'xtwitter':
        return 'x'
      case 'youtube':
      case 'instagram':
      case 'tiktok':
      case 'facebook':
      case 'snapchat':
      case 'linkedin':
      case 'twitch':
      case 'pinterest':
      case 'threads':
        return key
      default:
        return null
    }
  }

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="h-px bg-linear-to-r from-transparent via-foreground/20 to-transparent mb-3" />
      <h2 className="text-xs font-semibold text-[#FFFFFF]/60 uppercase tracking-wider mb-2">
        Your Social Channels
      </h2>

      <div className="flex flex-col gap-2">
        {channels.map((channel, index) => {
          const href = resolveSocialUrl(channel)

          return (
            <motion.a
              key={`${channel.platform}-${index}`}
              href={href || undefined}
              target={href ? '_blank' : undefined}
              rel={href ? 'noopener noreferrer' : undefined}
              className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-[#FFFFFF]/10 backdrop-blur-xs border border-[#FFFFFF]/10 hover:bg-[#FFFFFF]/15 transition-all"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.08 }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-7 h-7 rounded-full bg-[#FFFFFF]/10 border border-[#FFFFFF]/15 flex items-center justify-center shrink-0">
                  {toSimpleIconSlug(channel.platform) ? (
                    <SimpleIcon
                      slug={toSimpleIconSlug(channel.platform)!}
                      color="white"
                      height={14}
                      width={14}
                    />
                  ) : (
                    <span className="text-xs font-semibold text-[#FFFFFF]/80">
                      {channel.platform?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  )}
                </div>
                <span className="text-[#FFFFFF] text-sm font-medium">{channel.platform}</span>
                {channel.handle && (
                  <span className="text-[#FFFFFF]/70 text-sm truncate">
                    @{channel.handle.replace(/^@/, '')}
                  </span>
                )}
              </div>

              {channel.verified && <Check className="w-5 h-5 text-[#4d7c0f] shrink-0" />}
            </motion.a>
          )
        })}
      </div>
    </motion.section>
  )
}

export default ProfilePreview
